'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { RoleGuard } from '@/components/common/RoleGuard';
import Icon from '@/components/ui/AppIcon';
import ThemeToggle from '@/components/common/ThemeToggle';
import NotificationCenter from '@/components/common/NotificationCenter';

const VENDOR_NAV = [
  { label: 'Dashboard',           path: '/apps/vendor',                        icon: 'HomeIcon',                 tooltip: 'Store overview' },
  { label: 'Store Profile',       path: '/apps/vendor/store-profile',          icon: 'BuildingStorefrontIcon',   tooltip: 'Manage store profile' },
  { label: 'Products',            path: '/apps/vendor/product-management',     icon: 'CubeIcon',                 tooltip: 'Product catalog & inventory' },
  { label: 'Orders',              path: '/apps/vendor/order-fulfillment',      icon: 'ShoppingCartIcon',         tooltip: 'Order fulfillment' },
  { label: 'Analytics',           path: '/apps/vendor/analytics',              icon: 'ChartBarIcon',             tooltip: 'Sales analytics, best sellers, peak hours' },
  { label: 'Performance',         path: '/apps/vendor/performance',            icon: 'PresentationChartLineIcon', tooltip: 'Completion rate, prep time, ratings, revenue trends' },
  { label: 'Sponsored Listings',  path: '/apps/vendor/sponsored-listings',     icon: 'StarIcon',                 tooltip: 'Promote your store or products' },
  { label: 'Subscription',        path: '/apps/vendor/subscription',           icon: 'ReceiptPercentIcon',       tooltip: 'Subscription plan & commission rate' },
  { label: 'Stripe Setup',        path: '/apps/vendor/stripe-onboarding',      icon: 'CreditCardIcon',           tooltip: 'Stripe Connect onboarding' },
  { label: 'Payouts',             path: '/apps/vendor/stripe-payouts',         icon: 'BanknotesIcon',            tooltip: 'Stripe payout visibility' },
  { label: 'Notifications',       path: '/apps/vendor/notifications',          icon: 'BellIcon',                 tooltip: 'Notification preferences' },
];

export default function VendorLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, userProfile, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  
  // Login page must NOT be wrapped in RoleGuard — doing so causes a
  // "Verifying session..." loop because the guard redirects unauthenticated
  // users back to the same login URL it is currently rendering.
  if (pathname === '/apps/vendor/login') {
    return <>{children}</>;
  }


  const handleSignOut = async () => {
    await signOut();
    router.push('/apps/vendor/login');
  };

  const NavItem = ({ item, onClick }: { item: typeof VENDOR_NAV[0]; onClick?: () => void }) => {
    const isActive =
      pathname === item.path ||
      (item.path !== '/apps/vendor' && pathname.startsWith(item.path));
    return (
      <Link
        href={item.path}
        title={item.tooltip}
        onClick={onClick}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-foreground hover:bg-muted'
        }`}
      >
        <Icon name={item.icon as any} size={18} variant={isActive ? 'solid' : 'outline'} />
        {item.label}
      </Link>
    );
  };

  const UserFooter = ({ onSignOut }: { onSignOut: () => void }) => (
    <div className="p-4 border-t border-border">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
          <span className="text-primary text-xs font-bold">
            {userProfile?.full_name?.[0]?.toUpperCase() ||
              user?.email?.[0]?.toUpperCase() ||
              'V'}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-foreground truncate">
            {userProfile?.full_name || 'Vendor'}
          </p>
          <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
        </div>
      </div>
      <button
        onClick={onSignOut}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
      >
        <Icon name="ArrowRightOnRectangleIcon" size={14} />
        Sign Out
      </button>
    </div>
  );

  return (
    <RoleGuard requiredRole="vendor">
      <div className="min-h-screen bg-background flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex flex-col w-60 bg-card border-r border-border fixed top-0 left-0 bottom-0 z-50">
          <div className="p-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">Z</span>
              </div>
              <div>
                <p className="font-bold text-foreground text-sm">Zalchos Partner</p>
                <p className="text-xs text-muted-foreground">Vendor Portal</p>
              </div>
            </div>
          </div>
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {VENDOR_NAV.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </nav>
          <UserFooter onSignOut={handleSignOut} />
        </aside>

        {/* Mobile Header */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-1.5 rounded-md hover:bg-muted"
              aria-label="Toggle menu"
            >
              <Icon name="Bars3Icon" size={20} />
            </button>
            <span className="font-bold text-foreground text-sm">Zalchos Partner</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </div>

        {/* Mobile Drawer */}
        {mobileOpen && (
          <>
            <div
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileOpen(false)}
            />
            <aside className="lg:hidden fixed top-0 left-0 bottom-0 w-64 bg-card z-50 flex flex-col">
              <div className="p-5 border-b border-border flex items-center justify-between">
                <span className="font-bold text-foreground">Vendor Portal</span>
                <button onClick={() => setMobileOpen(false)}>
                  <Icon name="XMarkIcon" size={20} />
                </button>
              </div>
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {VENDOR_NAV.map((item) => (
                  <NavItem
                    key={item.path}
                    item={item}
                    onClick={() => setMobileOpen(false)}
                  />
                ))}
              </nav>
              <UserFooter onSignOut={handleSignOut} />
            </aside>
          </>
        )}

        {/* Main Content */}
        <main className="flex-1 lg:ml-60">
          {/* Desktop top bar */}
          <div className="hidden lg:flex items-center justify-between px-6 py-3 bg-card border-b border-border sticky top-0 z-30">
            <div />
            <div className="flex items-center gap-3">
              <ThemeToggle />
              {user && (
                <NotificationCenter
                  userId={user.id}
                  userType="vendor"
                  dropdownAlign="right"
                />
              )}
              <span className="text-xs text-muted-foreground">
                {userProfile?.full_name || user?.email}
              </span>
            </div>
          </div>
          <div className="mt-14 lg:mt-0 p-6">{children}</div>
        </main>
      </div>
    </RoleGuard>
  );
}
